import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, ExternalLink, Search, MapPin } from "lucide-react";

const emptySection = () => ({ type: "text", heading: "", body: "", image: "" });
const emptyPage = () => ({
  title: "", slug: "", hero_title: "", hero_subtitle: "", hero_image: "",
  sections: [emptySection()], published: true,
  seo: { meta_title: "", meta_description: "", keywords: [], geo_region: "", geo_city: "", geo_placename: "" },
});

export default function SitePageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !id;
  const [page, setPage] = useState(emptyPage());
  const [kw, setKw] = useState("");

  useEffect(() => {
    if (!isNew) {
      api.get(`/site/pages/${id}`).then((r) => {
        const p = r.data;
        setPage({ ...emptyPage(), ...p, seo: { ...emptyPage().seo, ...(p.seo || {}) } });
        setKw((p.seo?.keywords || []).join(", "));
      });
    }
  }, [id]);

  const setSeo = (k, v) => setPage((p) => ({ ...p, seo: { ...p.seo, [k]: v } }));
  const setSection = (i, k, v) => setPage((p) => {
    const s = [...p.sections]; s[i] = { ...s[i], [k]: v }; return { ...p, sections: s };
  });

  const save = async () => {
    if (!page.title) return toast.error("Le titre est requis");
    const payload = { ...page, seo: { ...page.seo, keywords: kw.split(",").map((x) => x.trim()).filter(Boolean) } };
    if (isNew) {
      const { data } = await api.post("/site/pages", payload);
      toast.success("Page créée");
      navigate(`/pages/${data.id}`, { replace: true });
    } else {
      await api.put(`/site/pages/${id}`, payload);
      toast.success("Page enregistrée");
    }
  };

  const publicUrl = page.slug ? `${window.location.origin}/site/${user?.company_id}/page/${page.slug}` : null;
  const Field = ({ label, value, onChange, placeholder, testid, textarea, hint }) => (
    <div>
      <label className="text-sm font-medium text-slate-700">{label}</label>
      {hint && <span className="text-xs text-slate-400 ml-2">{hint}</span>}
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} rows={3} placeholder={placeholder}
          className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
          className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
      )}
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-up max-w-4xl">
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => navigate("/pages")} className="inline-flex items-center gap-2 text-slate-500 hover:text-navy text-sm" data-testid="back-pages-btn">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <div className="flex gap-2">
          {!isNew && publicUrl && (
            <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="view-online-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm font-medium hover:border-sky-300">
              <ExternalLink className="w-4 h-4" /> Voir en ligne
            </a>
          )}
          <button onClick={save} data-testid="save-page-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep">
            <Save className="w-4 h-4" /> Enregistrer
          </button>
        </div>
      </div>

      {/* Contenu */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <h2 className="font-display font-bold text-navy text-xl">Contenu de la page</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Titre de la page" value={page.title} onChange={(v) => setPage({ ...page, title: v })} testid="page-title" placeholder="Ex : Sellerie sur mesure" />
          <Field label="Slug (URL)" value={page.slug} onChange={(v) => setPage({ ...page, slug: v })} testid="page-slug" placeholder="sellerie-sur-mesure" hint="généré si vide" />
        </div>
        <Field label="Titre hero (accroche)" value={page.hero_title} onChange={(v) => setPage({ ...page, hero_title: v })} testid="page-hero-title" placeholder="Grande accroche affichée en haut" />
        <Field label="Sous-titre hero" value={page.hero_subtitle} onChange={(v) => setPage({ ...page, hero_subtitle: v })} testid="page-hero-subtitle" textarea placeholder="Phrase de présentation" />
        <Field label="Image hero (URL)" value={page.hero_image} onChange={(v) => setPage({ ...page, hero_image: v })} testid="page-hero-image" placeholder="https://…" />

        <div className="pt-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-navy">Sections</h3>
            <button onClick={() => setPage({ ...page, sections: [...page.sections, emptySection()] })} data-testid="add-section-btn"
              className="inline-flex items-center gap-1.5 text-sm text-sky-600 font-semibold"><Plus className="w-4 h-4" /> Ajouter une section</button>
          </div>
          <div className="space-y-3">
            {page.sections.map((s, i) => (
              <div key={i} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-2" data-testid={`section-${i}`}>
                <div className="flex items-center gap-2">
                  <input value={s.heading} onChange={(e) => setSection(i, "heading", e.target.value)} placeholder="Titre de section" data-testid={`section-heading-${i}`}
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-sky-400" />
                  <button onClick={() => setPage({ ...page, sections: page.sections.filter((_, x) => x !== i) })} className="p-2 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
                </div>
                <textarea value={s.body} onChange={(e) => setSection(i, "body", e.target.value)} rows={3} placeholder="Contenu de la section" data-testid={`section-body-${i}`}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 pt-2 cursor-pointer">
          <input type="checkbox" checked={page.published} onChange={(e) => setPage({ ...page, published: e.target.checked })} data-testid="page-published" className="w-4 h-4 accent-sky-500" />
          <span className="text-sm text-slate-700">Page publiée (visible en ligne)</span>
        </label>
      </div>

      {/* SEO / GEO */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Search className="w-5 h-5 text-sky-500" />
          <h2 className="font-display font-bold text-navy text-xl">Référencement SEO & GEO</h2>
        </div>
        <p className="text-sm text-slate-500 -mt-2">Optimisez vos balises et mots-clés pour être n°1 sur Google et le référencement local.</p>
        <Field label="Balise titre (meta title)" value={page.seo.meta_title} onChange={(v) => setSeo("meta_title", v)} testid="seo-meta-title" placeholder="Titre affiché dans Google" hint="idéal 50-60 caractères" />
        <Field label="Meta description" value={page.seo.meta_description} onChange={(v) => setSeo("meta_description", v)} testid="seo-meta-description" textarea placeholder="Description affichée dans Google" hint="idéal 120-160 caractères" />
        <div>
          <label className="text-sm font-medium text-slate-700">Mots-clés</label>
          <span className="text-xs text-slate-400 ml-2">séparés par des virgules</span>
          <input value={kw} onChange={(e) => setKw(e.target.value)} data-testid="seo-keywords" placeholder="sellier garnisseur, bâche sur mesure, coussin…"
            className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-400" />
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Ville (GEO)" value={page.seo.geo_city} onChange={(v) => setSeo("geo_city", v)} testid="seo-geo-city" placeholder="Marseille" />
          <Field label="Région (GEO)" value={page.seo.geo_region} onChange={(v) => setSeo("geo_region", v)} testid="seo-geo-region" placeholder="PACA" />
          <Field label="Nom du lieu" value={page.seo.geo_placename} onChange={(v) => setSeo("geo_placename", v)} testid="seo-geo-placename" placeholder="Atelier ..." />
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500 bg-sky-50 rounded-xl p-3">
          <MapPin className="w-4 h-4 text-sky-600 shrink-0" />
          Les balises geo.region / geo.placename et les mots-clés locaux améliorent votre visibilité sur les recherches « près de chez moi ».
        </div>
      </div>
    </div>
  );
}
